<template>
  <div class="p-0">
    <div class="flex items-center justify-between mb-6 gap-4">
      <h1 class="text-2xl font-bold text-gray-800">Crear Tarea</h1>
      <router-link
        to="/tasks"
        class="text-sm text-gray-600 hover:text-gray-800 underline"
      >
        Volver a Mis tareas
      </router-link>
    </div>

    <div class="bg-white border border-gray-200 rounded-lg p-6">
      <div class="flex flex-col sm:flex-row gap-3 mb-5">
        <button
          v-if="canChooseInstitutionalTaskType"
          type="button"
          @click="chooseInstitutionalTaskType"
          :class="[
            'flex-1 sm:flex-none px-4 py-2 rounded-md font-semibold border transition-colors',
            task_type === 'institutional'
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50'
          ]"
        >
          Institucional
        </button>
        <button
          type="button"
          @click="task_type = 'personal'"
          :class="[
            'flex-1 sm:flex-none px-4 py-2 rounded-md font-semibold border transition-colors',
            task_type === 'personal'
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50'
          ]"
        >
          Personal
        </button>
      </div>

      <!-- Template -->
      <!-- <div class="mb-4">
        <label class="block text-xs font-medium text-gray-500 mb-1">Plantilla (Opcional)</label>
        <select
          v-model="template_id"
          @change="applySelectedTemplate"
          class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
        >
          <option value="">Sin plantilla</option>
          <option v-for="t in templates" :key="t.id" :value="t.id">
            {{ t.title }}
          </option>
        </select>
      </div> -->

      <!-- Title -->
      <div class="mb-4">
        <label class="block text-xs font-medium text-gray-500 mb-1">Título de la tarea</label>
        <input
          v-model="title"
          type="text"
          required
          class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
          placeholder="Ej.: Informe trimestral de ventas"
        />
      </div>

      <!-- Description -->
      <div class="mb-4">
        <label class="block text-xs font-medium text-gray-500 mb-1">Descripción</label>
        <textarea
          v-model="description"
          rows="4"
          required
          class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
          placeholder="Detalles específicos de la tarea y entregables esperados..."
        />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1">Categoría</label>
          <select
            v-model="category_id"
            required
            class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
          >
            <option value="" disabled>Seleccioná una categoría</option>
            <option v-for="c in categories" :key="c.id" :value="c.id">
              {{ c.name }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1">Fecha límite</label>
          <input
            v-model="due_at"
            type="datetime-local"
            required
            class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
          />
        </div>
      </div>

      <div class="mb-4">
        <label class="block text-xs font-medium text-gray-500 mb-1">Prioridad</label>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="p in priorities"
            :key="p.value"
            type="button"
            @click="priority = p.value"
            :class="[
              'px-3 py-2 rounded-md border text-sm font-semibold transition-colors',
              priority === p.value
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50'
            ]"
          >
            {{ p.label }}
          </button>
        </div>
      </div>

      <!-- Responsables -->
      <div class="mb-4" v-if="showResponsables">
        <div class="flex items-center justify-between gap-3 mb-2">
          <label class="block text-xs font-medium text-gray-500">Responsables</label>
          <button
            type="button"
            @click="clearResponsibles"
            class="text-xs text-gray-600 hover:text-gray-900 underline"
            v-if="responsibleUserIds.length"
          >
            Limpiar
          </button>
        </div>

        <div class="flex flex-wrap gap-2 mb-3">
          <span
            v-for="uid in responsibleUserIds"
            :key="uid"
            class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm bg-indigo-50 text-indigo-800 border border-indigo-100"
          >
            {{ teacherLabel(uid) }}
            <button
              type="button"
              class="text-indigo-600 hover:text-indigo-900"
              @click="removeResponsible(uid)"
              aria-label="Quitar responsable"
            >
              ×
            </button>
          </span>
          <span v-if="responsibleUserIds.length === 0" class="text-sm text-gray-500">
            Seleccioná responsables.
          </span>
        </div>

        <div class="flex flex-col gap-3">
          <template v-if="canChooseInstitutionalTaskType">
            <div class="flex flex-col sm:flex-row flex-wrap gap-3 sm:items-end">
              <div class="w-full sm:w-52">
                <label class="block text-xs font-medium text-gray-500 mb-1">Rol del responsable</label>
                <select
                  v-model="filterRoleId"
                  @change="onAddResponsibleRoleChange"
                  class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
                >
                  <option value="">Elegir rol…</option>
                  <option v-for="r in allRoles" :key="r.id" :value="r.id">
                    {{ roleOptionLabel(r.name) }}
                  </option>
                </select>
              </div>
              <div class="w-full sm:flex-1 sm:min-w-[12rem]">
                <label class="block text-xs font-medium text-gray-500 mb-1">Personas</label>
                <div
                  v-if="!filterRoleId"
                  class="text-sm text-gray-500 border border-gray-200 rounded-md px-3 py-2 bg-gray-50"
                >
                  Elegí un rol para listar personas.
                </div>
                <div
                  v-else-if="usersForResponsibleRole.length === 0"
                  class="text-sm text-gray-500 border border-gray-200 rounded-md px-3 py-2 bg-gray-50"
                >
                  No hay usuarios con ese rol.
                </div>
                <div
                  v-else
                  class="border border-gray-200 rounded-md max-h-48 overflow-y-auto p-2 space-y-1.5 bg-white"
                >
                  <template v-for="u in usersForResponsibleRole" :key="userRowKey(u)">
                    <label
                      v-if="userRowId(u)"
                      class="flex items-center gap-2 text-sm text-gray-800 cursor-pointer select-none"
                    >
                      <input
                        type="checkbox"
                        class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        :value="userRowId(u)"
                        v-model="pendingResponsibleUserIds"
                      />
                      <span>{{ userRowLabel(u) }}</span>
                    </label>
                  </template>
                </div>
              </div>
              <button
                type="button"
                class="w-full sm:w-auto px-4 py-2 text-sm font-medium bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:opacity-60 sm:mb-0.5"
                @click="addResponsible"
                :disabled="!filterRoleId || pendingResponsibleUserIds.length === 0"
              >
                Agregar
              </button>
            </div>
          </template>
          <div v-else class="flex flex-col gap-2">
            <label class="block text-xs font-medium text-gray-500">Docentes</label>
            <div
              v-if="teachers.length === 0"
              class="text-sm text-gray-500 border border-gray-200 rounded-md px-3 py-2 bg-gray-50"
            >
              No hay docentes cargados.
            </div>
            <div
              v-else
              class="border border-gray-200 rounded-md max-h-48 overflow-y-auto p-2 space-y-1.5 bg-white"
            >
              <label
                v-for="u in teachers"
                :key="u.id"
                class="flex items-center gap-2 text-sm text-gray-800 cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  class="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  :value="String(u.id)"
                  v-model="pendingResponsibleUserIds"
                />
                <span>{{ teacherLabel(u.id) }}</span>
              </label>
            </div>
            <button
              type="button"
              class="self-start px-4 py-2 text-sm font-medium bg-gray-800 text-white rounded-md hover:bg-gray-700 disabled:opacity-60"
              @click="addResponsible"
              :disabled="pendingResponsibleUserIds.length === 0"
            >
              Agregar
            </button>
          </div>
        </div>
      </div>

      <!-- Batch mode -->
      <div v-if="canChooseInstitutionalTaskType && task_type !== 'personal'" class="mb-5">
        <div class="flex items-center gap-3">
          <input id="batchMode" type="checkbox" :disabled="canChooseInstitutionalTaskType && task_type === 'institutional' " v-model="createBatchMode" />
          <label for="batchMode" class="text-sm text-gray-700 font-medium">
            Creación de lote activada
          </label>
        </div>
        <div v-if="createBatchMode" class="mt-2 bg-blue-50 border border-blue-200 text-blue-800 rounded-md px-4 py-3 text-sm">
          Se creará una tarea individual para cada uno de los {{ responsibleUserIds.length || 0 }} responsables seleccionados.
        </div>
      </div>

      <!-- Submit -->
      <div class="flex justify-end gap-3">
        <button
          type="button"
          @click="cancel"
          class="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          :disabled="saving"
        >
          Cancelar
        </button>
        <button
          type="button"
          @click="submit"
          class="px-6 py-2 text-sm font-semibold bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60"
          :disabled="saving"
        >
          {{ saving ? 'Creando...' : 'Crear Tareas' }}
        </button>
      </div>

      <div v-if="error" class="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CreateTaskView',
  data() {
    return {
      saving: false,
      loading: false,
      error: null,

      task_type: 'institutional',

      templates: [],
      categories: [],
      teachers: [],
      /** Solo admin: elegir responsable por rol + persona (misma API que TasksView) */
      allRoles: [],
      filterRoleId: '',
      usersForResponsibleRole: [],

      template_id: '',
      title: '',
      description: '',
      category_id: '',
      priority: 'media',
      due_at: '',

      responsibleUserIds: [],
      /** Ids marcados en la lista (checkboxes) antes de pulsar Agregar */
      pendingResponsibleUserIds: [],
      /** Nombres ya agregados (p. ej. tras cambiar de rol, la lista por rol ya no incluye a esa persona) */
      responsibleLabelCache: {},

      createBatchMode: false,

      priorities: [
        { value: 'baja', label: 'BAJA' },
        { value: 'media', label: 'MEDIA' },
        { value: 'alta', label: 'ALTA' },
        { value: 'urgente', label: 'URGENTE' },
      ],
    };
  },
  computed: {
    isAdmin() {
      return this.$store.state.authentication?.user?.role === 'admin';
    },
    /** Misma regla que enlaces Lotes / Creadas por mí en TasksView */
    canChooseInstitutionalTaskType() {
      const role = this.$store.state.authentication?.user?.role;
      console.log("role  .... ", role)
      return ['admin', 'executive', 'administrative', 'director'].includes(role);
    },
    showResponsables() {
      // Para institucional y/o lote, se requieren responsables.
      return this.createBatchMode || this.task_type === 'institutional';
    },
  },
  watch: {
    task_type(val) {
      if (val === 'personal') {
        this.createBatchMode = false;
      }
    },
  },
  created() {
    if (!this.canChooseInstitutionalTaskType) {
      this.task_type = 'personal';
      this.createBatchMode = false;
    } else if (this.$route.query.batch === '1') {
      this.createBatchMode = true;
    }
    this.fetchAll();
  },
  mounted(){
    if(this.canChooseInstitutionalTaskType) {
      this.createBatchMode = true
    }
  },
  methods: {
    chooseInstitutionalTaskType(){
      this.task_type = 'institutional'
      this.createBatchMode = true
    },
    unwrapData(resp) {
      return resp?.data?.data ?? resp?.data?.data?.data ?? resp?.data ?? [];
    },
    async fetchAll() {
      this.loading = true;
      this.error = null;
      try {
        const requests = [
          this.$http.get('/task-categories', { params: { active_only: true } }),
          this.$http.get('/task-templates', { params: { active_only: true } }),
          this.$http.get('/teachers'),
        ];
        if (this.canChooseInstitutionalTaskType) {
          requests.push(
            this.$http.get('/roles').catch(() => ({ data: { data: [] } }))
          );
        }
        const results = await Promise.all(requests);
        const catsRes = results[0];
        const templRes = results[1];
        const teachersRes = results[2];
        const rolesRes = this.canChooseInstitutionalTaskType ? results[3] : null;

        const cats = this.unwrapData(catsRes);
        const templ = this.unwrapData(templRes);
        const teachers = this.unwrapData(teachersRes);

        this.categories = Array.isArray(cats) ? cats : cats?.data || [];
        this.templates = Array.isArray(templ) ? templ : templ?.data || [];
        // /teachers suele devolver array en res.data.data
        this.teachers = Array.isArray(teachers) ? teachers : teachers?.data || [];

        if (this.canChooseInstitutionalTaskType && rolesRes) {
          const raw = rolesRes.data?.data ?? rolesRes.data;
          let list = Array.isArray(raw) ? raw : [];
          
          const filtered = ['teacher', 'student', 'rti', 'director', 'janitor']
        
          list =  list.filter(el => !filtered.includes(el.name))
          this.allRoles = [...list].sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'es')
          );
        }
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || 'Error al cargar datos del formulario';
      } finally {
        this.loading = false;
      }
    },
    roleOptionLabel(name) {
      const n = (name || '').toLowerCase();
      const map = {
        admin: 'Referente Tecnico',
        administrative: 'Administrativo',
        executive: 'Directivo',
        teacher: 'Docente',
        director: 'Director',
        janitor: 'Personal de limpieza',
        student_assistant: 'Preceptor/a',
      };
      return map[n] || name || '—';
    },
    userRowId(entry) {
      if (!entry) return '';
      const u = entry.User?.id ?? entry.user_id ?? entry.id;
      return u != null && u !== '' ? String(u) : '';
    },
    userRowKey(entry) {
      const id = this.userRowId(entry);
      return id || String(entry?.id ?? '');
    },
    userRowLabel(entry) {
      if (!entry) return '';
      if (entry.Profile && (entry.Profile.lastname || entry.Profile.firstname)) {
        return `${entry.Profile.lastname || ''}, ${entry.Profile.firstname || ''}`.trim();
      }
      return entry.name || entry.email || this.userRowId(entry) || '—';
    },
    async loadUsersForRole(roleId) {
      if (!roleId) {
        this.usersForResponsibleRole = [];
        return;
      }
      try {
        const res = await this.$http.get(`/user-by-role-id/${roleId}`);
        const raw = res.data?.data ?? res.data ?? [];
        this.usersForResponsibleRole = Array.isArray(raw) ? raw : [];
      } catch {
        this.usersForResponsibleRole = [];
      }
    },
    async onAddResponsibleRoleChange() {
      this.pendingResponsibleUserIds = [];
      this.usersForResponsibleRole = [];
      if (!this.filterRoleId) return;
      await this.loadUsersForRole(this.filterRoleId);
    },
    teacherLabel(id) {
      if (id == null || id === '') return '—';
      const sid = String(id);
      if (this.responsibleLabelCache[sid]) return this.responsibleLabelCache[sid];
      const t = this.teachers.find((x) => String(x.id) === sid);
      if (t) {
        const lname = t.Profile?.lastname || '';
        const fname = t.Profile?.firstname || '';
        return (lname || fname) ? `${lname}, ${fname}`.trim() : t.name || t.email || sid;
      }
      const row = this.usersForResponsibleRole.find((e) => this.userRowId(e) === sid);
      if (row) return this.userRowLabel(row);
      return sid;
    },
    clearResponsibles() {
      this.responsibleUserIds = [];
      this.responsibleLabelCache = {};
    },
    removeResponsible(uid) {
      const sid = String(uid);
      this.responsibleUserIds = this.responsibleUserIds.filter((id) => String(id) !== sid);
      delete this.responsibleLabelCache[sid];
    },
    cacheResponsibleLabel(sid) {
      const row = this.usersForResponsibleRole.find((e) => this.userRowId(e) === sid);
      if (row) {
        this.responsibleLabelCache[sid] = this.userRowLabel(row);
        return;
      }
      const t = this.teachers.find((x) => String(x.id) === sid);
      if (t) {
        const lname = t.Profile?.lastname || '';
        const fname = t.Profile?.firstname || '';
        this.responsibleLabelCache[sid] = (lname || fname)
          ? `${lname}, ${fname}`.trim()
          : t.name || t.email || sid;
      }
    },
    addResponsible() {
      const toAdd = [...this.pendingResponsibleUserIds];
      if (!toAdd.length) return;
      for (const uid of toAdd) {
        if (uid == null || uid === '') continue;
        if (this.responsibleUserIds.some((id) => String(id) === String(uid))) continue;
        const sid = String(uid);
        this.cacheResponsibleLabel(sid);
        this.responsibleUserIds.push(sid);
      }
      this.pendingResponsibleUserIds = [];
    },
    applySelectedTemplate() {
      const selected = this.templates.find((t) => String(t.id) === String(this.template_id));
      if (!selected) return;
      this.title = selected.title || '';
      this.description = selected.description || '';
      this.category_id = selected.category_id || selected.category?.id || '';
      this.priority = selected.default_priority || selected.priority || 'media';
    },
    toISO(value) {
      if (!value) return null;
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return null;
      return d.toISOString();
    },
    cancel() {
      this.$router.push('/tasks');
    },
    validateRequiredFields() {
      if (!this.title.trim()) {
        this.error = 'El título es obligatorio.';
        return false;
      }
      if (!this.description.trim()) {
        this.error = 'La descripción es obligatoria.';
        return false;
      }
      if (!this.category_id) {
        this.error = 'La categoría es obligatoria.';
        return false;
      }
      if (!this.due_at) {
        this.error = 'La fecha y hora límite es obligatoria.';
        return false;
      }
      return true;
    },
    async submit() {
      this.saving = true;
      this.error = null;
      try {
        if (!this.validateRequiredFields()) {
          return;
        }
        if (!this.createBatchMode && this.task_type === 'institutional' && this.responsibleUserIds.length === 0) {
          this.error = 'Seleccioná al menos un responsable para tareas institucionales.';
          return;
        }
        if (this.createBatchMode && this.responsibleUserIds.length === 0) {
          this.error = 'Seleccioná al menos un responsable para crear un lote.';
          return;
        }

        const dueAt = this.toISO(this.due_at);

        if (this.createBatchMode) {
          // Create batch of tasks
          await this.$http.post('/task-batches', {
            title: this.title,
            description: this.description,
            template_id: this.template_id || null,
            category_id: this.category_id || null,
            default_priority: this.priority,
            due_at: dueAt,
            responsible_user_ids: this.responsibleUserIds,
          });
          this.$router.push('/task-batches');
          return;
        }

        // Create single task
        const payload = {
          task_type: this.task_type,
          title: this.title,
          description: this.description,
          template_id: this.template_id || null,
          category_id: this.category_id || null,
          priority: this.priority,
          due_at: dueAt,
          is_private: false,
        };

        if (this.task_type === 'institutional') {
          payload.responsible_user_id = this.responsibleUserIds[0];
        }

        await this.$http.post('/tasks', payload);
        this.$router.push('/tasks');
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || 'Error al crear la tarea';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
</style>

